<template>
  <BaseTabInterface
    :class="{ 'load load--fill': isLoading }"
    :tabs="tabs"
    :initial-tab="tabs[0]"
  >
    <div
      slot-scope="{ activeTab, changeActiveTab }"
      class="tabs"
    >
      <ul class="tabs__tab md:inline-flex">
        <BaseTab
          v-for="(tab, index) in tabs"
          :key="tab.slug"
        >
          <li
            class="w-full md:w-auto"
            :class="{ 'current': activeTab === tab.slug }"
          >
            <a
              :id="`tab-${tab.slug}`"
              href="#"
              :aria-selected="activeTab === tab.slug"
              @click.prevent="changeActiveTab(tab.slug)"
              v-text="`${index + 1}. ${tab.title}`"
            />
          </li>
        </BaseTab>
      </ul>
      <div class="tabs__tab_content py-8">
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="content"
        >
          <section class="max-w-lg mx-auto">
            <form @submit.prevent="setBody(changeActiveTab)">
              <fieldset>
                <legend
                  class="fieldset-legend"
                  v-text="'Content'"
                />
                <div class="field-list">
                  <div class="field">
                    <textarea
                      id="post-content"
                      v-model="sms.body"
                      name="post-content"
                      class="mb-0"
                      :class="{ 'error': hasError('body') }"
                      rows="3"
                      :maxlength="lengthLimit"
                    />
                    <div class="field__meta">
                      <span
                        v-if="hasError('body')"
                        class="field__meta__help"
                        v-text="errors.body[0]"
                      />
                    </div>
                    <p
                      class="text-xs italic mt-0 text-right"
                      :class="[smsTooLong ? 'text-red' : 'text-dark-grey']"
                      v-text="`${smsLength}/${lengthLimit} characters`"
                    />
                  </div>
                </div>
              </fieldset>
              <div class="text-center">
                <button
                  type="submit"
                  class="btn"
                  :disabled="smsLength > lengthLimit || !enabled"
                  v-text="'Next'"
                />
              </div>
            </form>
          </section>
        </BaseTabContent>
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="recipients"
        >
          <section>
            <div class="max-w-lg mx-auto lg:flex">
              <div class="w-full lg:w-1/2 lg:pr-8">
                <RecipientGroupSelector v-model="selectedGroupUuids" />
              </div>
              <div class="w-full lg:w-1/2 lg:pl-8">
                <RecipientMembershipSelector
                  v-model="selectedMembershipUuids"
                  communication-mode="sms"
                />
              </div>
            </div>
            <div class="text-center mt-6">
              <button
                type="button"
                class="btn"
                :disabled="!enabled"
                :aria-disabled="!enabled"
                :class="{ load: isLoading }"
                @click="changeActiveTab('publish')"
                v-text="'Next'"
              />
            </div>
          </section>
        </BaseTabContent>
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="publish"
        >
          <section class="max-w-lg mx-auto">
            <div class="md:flex">
              <div class="w-full md:w-2/3 md:pr-8">
                <h3 v-text="'Content'" />
                <div
                  v-if="hasContent"
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-grey rounded italic"
                  @click.prevent="changeActiveTab('content')"
                  v-text="sms.body"
                />
                <div
                  v-else
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                  @click.prevent="changeActiveTab('content')"
                  v-text="'No content provided!'"
                />
                <h3 v-text="'Recipients'" />
                <div
                  v-if="hasRecipients"
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-grey rounded italic"
                  @click.prevent="changeActiveTab('recipients')"
                >
                  <ul class="list-reset p-0 m-0">
                    <li v-text="`Membership Groups: ${selectedGroupUuids.length}`" />
                    <li>
                      Individual Memberships: {{ selectedMembershipUuids.includes('send_to_all_members') ? 'All Members' : selectedMembershipUuids.length }}
                    </li>
                  </ul>
                </div>
                <div
                  v-else
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                  @click.prevent="changeActiveTab('recipients')"
                  v-text="'No recipients have been selected!'"
                />
              </div>
              <div class="w-full md:w-1/3">
                <h3 v-text="'Scheduling'" />
                <p
                  class="mt-0"
                  v-text="'When would you like to send this text message?'"
                />
                <div class="flex items-start mb-4">
                  <div class="flex mr-4">
                    <input
                      id="now"
                      v-model="deliverNow"
                      type="radio"
                      name="now"
                      :value="true"
                    >
                    <label
                      for="now"
                      v-text="'Now'"
                    />
                  </div>
                  <div class="flex">
                    <input
                      id="later"
                      v-model="deliverNow"
                      type="radio"
                      name="later"
                      :value="false"
                    >
                    <label
                      for="later"
                      v-text="'Later'"
                    />
                  </div>
                </div>
                <BaseDatePicker
                  v-if="!deliverNow"
                  v-model="deliverAt"
                  :config="{ minDate: 'today' }"
                  placeholder="Choose date"
                />
                <small
                  v-if="!deliverNow"
                  v-text="`Scheduling is based in the ${timezone} timezone.`"
                />
              </div>
            </div>
            <div class="mt-6 text-center">
              <button
                type="button"
                class="btn"
                :disabled="!readyForSending && !enabled"
                :aria-disabled="!readyForSending && !enabled"
                @click="send"
                v-html="sendButtonText"
              />
              <a
                href="#"
                class="ml-6"
                @click.prevent="saveDraft"
                v-text="'Save draft'"
              />
            </div>
          </section>
        </BaseTabContent>
      </div>
    </div>
  </BaseTabInterface>
</template>

<script type="text/babel">
import BaseTab from 'Base/Resources/assets/js/components/BaseTab'
import BaseTabContent from 'Base/Resources/assets/js/components/BaseTabContent'
import BaseDatePicker from 'Base/Resources/assets/js/components/BaseDatePicker'
import RecipientGroupSelector from './RecipientGroupSelector'
import RecipientMembershipSelector from './RecipientMembershipSelector'

export default {
  name: 'SMSForm',
  components: {
    BaseTab,
    BaseTabContent,
    BaseDatePicker,
    RecipientGroupSelector,
    RecipientMembershipSelector,
  },
  props: {
    smsUuid: {
      type: String,
      required: false,
      default: '',
    },
    timezone: {
      type: String,
      required: true,
    },
    enabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      tabs: [
        {
          slug: 'content',
          title: 'Content',
        },
        {
          slug: 'recipients',
          title: 'Recipients',
        },
        {
          slug: 'publish',
          title: 'Publish',
        },
      ],
      isLoading: false,
      sms: {
        uuid: null,
        body: '',
      },
      deliverNow: true,
      errors: {},
      lengthLimit: 160,
      deliverAt: null,
      selectedMembershipUuids: [],
      selectedGroupUuids: [],
    }
  },
  computed: {
    exists() {
      return !!this.sms.uuid
    },
    hasRecipients() {
      return !!(this.selectedMembershipUuids.length || this.selectedGroupUuids.length)
    },
    hasContent() {
      return !!this.sms.body
    },
    smsLength() {
      return this.sms.body.length
    },
    readyForSending() {
      if (this.deliverNow) {
        return this.hasRecipients && this.hasContent
      }

      return this.hasRecipients && this.hasContent && this.deliverAt
    },
    sendButtonText() {
      return this.deliverNow ? 'Send now' : 'Schedule'
    },
    smsTooLong() {
      return this.smsLength > (this.lengthLimit - 20)
    },
  },
  async mounted() {
    await this.$nextTick()

    this.sms.uuid = this.smsUuid

    if (this.smsUuid) {
      this.isLoading = true

      this.$store.dispatch('communicationSetSMS', {
        uuid: this.sms.uuid,
        includes: 'memberRecipients,groupRecipients',
      }).then((response) => {
        this.fillForm(response.data)

        this.isLoading = false
      })
    }
  },
  methods: {
    setBody(callback) {
      callback('recipients')
    },
    saveDraft() {
      this.save().then(() => {
        this.$store.commit('baseAddNotification', {
          type: 'success',
          title: 'Success',
          text: 'Text message draft saved!',
        })
      })
    },
    save() {
      return new Promise((resolve, reject) => {
        this.isLoading = true

        let action = 'post'
        let url = 'communications/sms'

        if (this.exists) {
          action = 'put'
          url = `communications/sms/${this.sms.uuid}`
        }

        this.$api.active()[action](url, this.buildPayload()).then((response) => {
          if (!this.exists) {
            this.sms.uuid = response.data.uuid

            history.pushState(null, null, `/communications/sms/${this.sms.uuid}/edit`)
          }

          this.isLoading = false

          resolve(response)
        }).catch((error) => {
          this.errors = error.response.data.errors
          this.isLoading = false

          reject(error)
        })
      })
    },
    buildPayload() {
      const payload = {}

      payload.body = this.sms.body
      payload.membership_group_uuids = this.selectedGroupUuids
      payload.membership_uuids = this.selectedMembershipUuids

      if (!this.deliverNow) {
        payload.delivery_date = this.deliverAt
      }

      return payload
    },
    send() {
      return this.save()
        .then(() => {
          this.deliver()
        })
    },
    deliver() {
      this.isLoading = true

      this.$api.active().post(`communications/sms/${this.sms.uuid}/send`, {
        delivery_date: this.deliverNow ? 'now' : this.deliverAt,
      }).then(() => {
        window.location = this.$urlGenerator.active(`communications/sms/${this.sms.uuid}`)
      }).catch(() => {
        this.$store.commit('baseAddNotification', {
          type: 'error',
          title: 'Error',
          text: 'There was a problem sending the text message.',
        })

        this.isLoading = false
      })
    },
    fillForm(sms) {
      this.sms.body = sms.attributes.body
      this.deliverAt = sms.attributes.delivery_date ? sms.attributes.delivery_date : 'now'
      this.deliverNow = !sms.attributes.delivery_date
      this.selectedGroupUuids = _.map(sms.groupRecipients.data, 'uuid')
      this.selectedMembershipUuids = _.map(sms.memberRecipients.data, 'uuid')
    },
    hasError(field) {
      return this.errors.hasOwnProperty(field)
    },
  },
}
</script>
